import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { useRecoilValue } from "recoil"
import { MyList } from "../../api-clients/mylist-api/api"
import { MAXIMUM_ALLOWED_ARTICLES_INLISTS } from "../constants/constants"
import { productListStatus } from "../constants/search-constants"
import { defaultListIdState } from "../store/defaultListIdStore"
import { AddToListStatusType } from "../types/global"
import { addItemToList, createDefaultList, isArticleInList, isProductInList } from "../utils/my-list-utils"
import { checkIsProduct } from "../utils/product-utils"
import { useBrandedTranslation } from "./useBrandedTranslation"
import useList from "./useList"
import useUserLists from "./useUserLists"

type UseAddToListReturnTypes = {
  addToList: () => void
  isInList: (itemId: string, listData: MyList) => boolean
  isBeingAdded: boolean
  isAdded: boolean
  isNotAdded: boolean
  isAtArticleLimit: boolean
}

const useAddToList = (itemId: string, isPatch = false): UseAddToListReturnTypes => {
  const { t } = useBrandedTranslation()
  const defaultListId = useRecoilValue(defaultListIdState)
  const [status, setStatus] = useState<AddToListStatusType>(productListStatus.NOT_ADDED)

  const router = useRouter()
  const { locale, query } = router
  const brand = query.brand as string

  const { createList, isUserLoggedIn } = useUserLists()
  const { listData, updateList: putUpdateList, patchUpdateList } = useList(defaultListId)

  const updateList = isPatch ? patchUpdateList : putUpdateList

  const hasList = isUserLoggedIn ? !!listData?.userId : !!listData?.id && !listData?.userId
  const listCount = listData?.totalArticles ?? 0

  /* Check if the totalArticles count for the default list exceeds the maximum allowed for one list */
  const isAtArticleLimit = listCount >= MAXIMUM_ALLOWED_ARTICLES_INLISTS
  const isBeingAdded = status === productListStatus.IS_BEING_ADDED
  const isNotAdded = status === productListStatus.NOT_ADDED
  const isAdded = status === productListStatus.ADDED

  /* Check if the item to be added to the list is a product or an article */
  const isProduct = checkIsProduct(itemId)

  /* Define function handlers to be either product or article related */
  const isInList = isProduct ? isProductInList : isArticleInList

  /* Check if the item is already in the list and set the appropriate status */
  useEffect(() => {
    /* Guard clause if there is still no initialized default list for the user  */
    if (isBeingAdded || !hasList) return

    const currentStatus = isInList(itemId, listData) ? productListStatus.ADDED : productListStatus.NOT_ADDED

    setStatus(currentStatus)
  }, [hasList, listCount, itemId, defaultListId])

  useEffect(() => {
    if (!isBeingAdded) return

    /* If user doesn't have a list yet, create one for him.
      Or if the user is logged in but he has only an anonymous list, create a user list. */
    if (!hasList) {
      createDefaultList({
        createList,
        isUserLoggedIn,
        anonymousList: listData,
        defaultName: t("wishlist-default-name"),
      })
      return
    }

    /* Check if the item is already in the list.
      Simulate a quick api call for better user experience if it's already in.
      Status will be updated on load as a further improvement on a later stage. */
    if (isInList(itemId, listData)) {
      setTimeout(() => setStatus(productListStatus.ADDED), 200)
      return
    }

    /* If a list is already available for the user, use it to add the article to it. */
    addItemToList({ itemId, listData, locale, brand, updateList })
      .then(() => {
        setStatus(productListStatus.ADDED)
      })
      .catch(() => {
        setStatus(productListStatus.NOT_ADDED)
      })
  }, [status, hasList])

  const addToList = () => {
    if (isNotAdded && !isAtArticleLimit) setStatus(productListStatus.IS_BEING_ADDED)
  }

  return {
    addToList,
    isInList,
    isBeingAdded,
    isAdded,
    isNotAdded,
    isAtArticleLimit,
  }
}

export default useAddToList
